import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { ChallengesService } from '../challenges.service';
import { CreatableChallenge, MetricDropdown } from '../challenge.model';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import moment from 'moment';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'cxone-challenge-create',
  templateUrl: './cxone-challenge-create.component.html',
  styleUrls: ['./cxone-challenge-create.component.scss']
})
export class CxoneChallengeCreateComponent implements OnInit {
  challengeForm: FormGroup;
  metric = null;
  inviteParticipant = null;
  schedule = null;
  startDate = new Date();
  wagerAmount = "0";
  metrics = [];
  participants = [];
  minDate = new Date();
  disabledDates: any = [];
  isSameDayActive = false;
  availableCoins = 0;
  availableMetrics: CreatableChallenge[] = [];

  constructor(
    private fb: FormBuilder,
    private challengeService: ChallengesService,
    private cvLocalizationService: CVLocalizationService,
    private decimalPipe: DecimalPipe
  ) {
    this.availableCoins = this.challengeService.availableCoins;
    this.availableMetrics = this.challengeService.availableMetrics;
  }

  ngOnInit() {
    this.challengeForm = this.fb.group({
      metric: new FormControl(this.metric, [Validators.required]),
      inviteParticipant: new FormControl(this.inviteParticipant, [Validators.required]),
      schedule: new FormControl(this.schedule, [Validators.required]),
      startDate: new FormControl(this.startDate, [Validators.required]),
      wagerAmount: new FormControl(this.wagerAmount, [Validators.required, Validators.min(0), Validators.pattern("^[0-9,]+$"), this.maxValueValidator()])
    });

    this.formatMetric();
    
    this.challengeForm.get('metric').valueChanges.subscribe(value => {
      const schedule = this.challengeForm.get('schedule').value
      this.isSameDayActive = value.activeSDC;
      if (schedule) {
        this.onMetricScheduleChange(schedule, value);
      }
    });

    this.challengeForm.get('schedule').valueChanges.subscribe(value => {
      const metric = this.challengeForm.get('metric').value;
      if (metric) {
        this.onMetricScheduleChange(value, metric);
      }
    });
  }

  validateWagerInput(event: KeyboardEvent, control: AbstractControl) {
    const inputValue = (event.target as HTMLInputElement).value;
    const regExp = new RegExp("^[0-9,]+$");
    if (!regExp.test(inputValue)) {
      setTimeout(() => {
        let sanitizedContent = inputValue.replace(/[^\d,]/g, '');
        let digitsOnly = sanitizedContent.replace(/,/g, '');
        digitsOnly = digitsOnly.replace(/^0+/, '');
        const formattedValue = this.decimalPipe.transform(parseFloat(digitsOnly), undefined, CVCultureName);
        control.setValue(formattedValue);

      }, 0);
    }
  }

  onMetricScheduleChange(schedule: string, metric: MetricDropdown ) {
    this.participants = [];
    this.fetchInvitableUsers(schedule === 'Daily' ? metric.dayRuleId : metric.weekRuleId);
    if (schedule === 'Daily') {
      this.minDate = this.isSameDayActive ? moment().toDate() : moment().add(1, 'days').toDate();
      this.disabledDates = [];
      // set the start date to the next day if the current day is not the same day
      if (!this.isSameDayActive) {
        setTimeout(() => {
          this.challengeForm.get("startDate").setValue(moment().add(1, 'days').toDate());
        }, 0);
      } else {
        setTimeout(() => {
          this.challengeForm.get("startDate").setValue(moment().toDate());
        }, 0);
      }
    } else {
      this.minDate = moment().isoWeekday(2).toDate();
      this.disabledDates = [0, 2, 3, 4, 5, 6];
      // set the start date to the next monday if the current day is not monday
      this.challengeForm.get("startDate").setValue(moment().add(1, 'weeks').isoWeekday(1).toDate());
    }
  }

  onDateChange(event) {
    // validate the selected date
    const selectedDate = moment(event);
    const currentDate = moment();
    const schedule = this.challengeForm.get('schedule').value;

    const setStartDate = (date: moment.Moment) => {
      this.challengeForm.get("startDate").setValue(date.toDate());
    };
    
    const getNextDay = () => moment().add(1, 'days');
    const getNextMonday = () => moment().add(1, 'weeks').isoWeekday(1);

    if (schedule === 'Daily') {
      if (!moment(event).isValid() || selectedDate.isBefore(currentDate, 'day')) {
        setStartDate(this.isSameDayActive ? moment() : getNextDay());
      } else if (!this.isSameDayActive && selectedDate.isSame(currentDate, 'day')) {
        setStartDate(getNextDay());
      }
    } else {
      if (!moment(event).isValid() || selectedDate.isBefore(currentDate, 'day')) {
        setStartDate(getNextMonday());
      } else if (selectedDate.isoWeekday() !== 1) {
        const currentMonday = moment().startOf('isoWeek').isoWeekday(1);
        // Check if the selected date is in the same week as the current Monday
        if (selectedDate.isSame(currentMonday, 'week')) {
          // if it is the same week, select the next Monday
          setStartDate(currentMonday.add(1, 'week'));
        } else {
          // if it is not the same week, select the monday of the selected date's week
          setStartDate(selectedDate.startOf('isoWeek').isoWeekday(1));
        }
      }
    }
  }

  formatMetric() {
    this.metric = this.availableMetrics.map(metric => {
      return {
        name: metric.MetricName,
        value: metric.MetricID,
        dayRuleId: metric.DayRuleID,
        weekRuleId: metric.WeekRuleID,
        activeSDC: metric.ActiveSDC
      }
    });
  }

  fetchInvitableUsers(ruleID: number) {
    this.challengeService.loadInvitableUsers(ruleID).then((data) => {
      this.participants = data.map(user => {
        return {
          name: user.LoginName,
          value: user.LoginID
        }
      });
    }).catch((error) => {
      console.error('error fetching invitable users:', error);
    });
  }

  displayError(control: AbstractControl): boolean {
    return control && control.errors && (control.touched || control.dirty);
  }

  getErrorMessage(control: AbstractControl): string {
    let errorMessage = '';
    if (!control) {
      return errorMessage;
    }

    errorMessage = control.hasError('required') ? this.getTranslation("Field_Required", "This field is required") : errorMessage;
    errorMessage = control.hasError('min') ?  this.getTranslation("Minimum_Value", "Minimum value is") + " 0" : errorMessage;
    errorMessage = control.hasError('max') ? this.getTranslation("Minimum_Value", "Minimum value is") + " " + this.availableCoins : errorMessage;
    return errorMessage;
  }

  getTranslation(key: string, defaultValue: string): string {
    return this.cvLocalizationService.getCachedText('Challenges', key, defaultValue);
  }

  validateClipboardInput(event: ClipboardEvent, control: AbstractControl): void {
    event.preventDefault();
    const pastedContent = event.clipboardData.getData('text');
    let sanitizedContent = pastedContent.replace(/[^\d,]/g, '');
    let digitsOnly = sanitizedContent.replace(/,/g, '');
    if (digitsOnly.length > 10) {
        digitsOnly = digitsOnly.substring(0, 10);
    }
    setTimeout(() => {
        digitsOnly = digitsOnly.replace(/^0+/, ''); // Remove leading zeros
        const formattedValue = this.decimalPipe.transform(parseFloat(digitsOnly), undefined, CVCultureName);
        control.setValue(formattedValue);
    }, 0);
  }

  maxValueValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value && typeof control.value === 'string' && control.value.match(/^[0-9,]+$/)) {
        // Remove commas and convert to number
        const wagerAmount = parseFloat(control.value.replace(/,/g, ''));
        return wagerAmount > this.availableCoins ? { 'maxValue': { value: control.value } } : null;
      }
      return null; // Return null if control.value does not exist or is not a number
    };
  }
}